import React, { useRef, useState, useEffect, useCallback } from 'react';
import TableLoader from '../loaders/TableLoader';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Database } from 'lucide-react';

export interface TableColumn<T = unknown> {
    key: string;
    header: React.ReactNode | string;
    render?: (item: T, index: number) => React.ReactNode;
    className?: string;
    headerClassName?: string;
}

export interface Action<T> {
    text: string;
    icon: React.ReactNode;
    onClick: (row: T) => void;
}

export interface TableProps<T = unknown> {
    columns: TableColumn<T>[];
    data: T[];
    showCheckbox?: boolean;
    onSelectAll?: (checked: boolean) => void;
    onSelectItem?: (item: T, checked: boolean, index?: number) => void;
    selectedItems?: Set<string | number>;
    getItemId?: (item: T) => string | number;
    isItemSelectable?: (item: T) => boolean;
    className?: string;
    tableClassName?: string;
    minHeight?: string;
    emptyMessage?: string;
    loading?: boolean;
    actions?: Action<T>[] | ((item: T) => Action<T>[]);
    /** Number of columns from the left to make sticky */
    stickyColumns?: number;
}

const CommonTable = <T extends Record<string, any>>({
    columns,
    data,
    showCheckbox = false,
    onSelectAll,
    onSelectItem,
    selectedItems = new Set(),
    getItemId = (item) => item.id,
    isItemSelectable,
    className = '',
    tableClassName = '',
    minHeight = '',
    emptyMessage = 'No data available',
    loading = false,
    actions,
    stickyColumns = 0,
}: TableProps<T>) => {
    const headerRowRef = useRef<HTMLTableRowElement>(null);
    const [stickyOffsets, setStickyOffsets] = useState<number[]>([]);

    const computeOffsets = useCallback(() => {
        if (stickyColumns <= 0 || !headerRowRef.current) return;
        const ths = headerRowRef.current.querySelectorAll('th');
        const offsets: number[] = [];
        let cumulative = 0;
        const startIdx = showCheckbox ? 1 : 0; // skip checkbox column
        for (let i = startIdx; i < startIdx + stickyColumns && i < ths.length; i++) {
            offsets.push(cumulative);
            cumulative += ths[i].getBoundingClientRect().width;
        }
        setStickyOffsets(offsets);
    }, [stickyColumns, showCheckbox]);

    useEffect(() => {
        computeOffsets();
    }, [computeOffsets, columns, data, loading]);

    useEffect(() => {
        if (stickyColumns <= 0) return;
        window.addEventListener('resize', computeOffsets);
        return () => window.removeEventListener('resize', computeOffsets);
    }, [stickyColumns, computeOffsets]);

    const getStickyStyle = (colIndex: number, isHeader = false): React.CSSProperties | undefined => {
        if (stickyColumns <= 0 || colIndex >= stickyColumns || colIndex >= stickyOffsets.length) return undefined;
        return {
            position: 'sticky',
            left: stickyOffsets[colIndex],
            zIndex: isHeader ? 3 : 1,
        };
    };

    const getStickyClassName = (colIndex: number, isHeader = false): string => {
        if (stickyColumns <= 0 || colIndex >= stickyColumns || colIndex >= stickyOffsets.length) return '';
        const borderClass = colIndex === stickyColumns - 1 ? '' : '';
        if (isHeader) return `bg-slate-50 ${borderClass}`;
        return `bg-white ${borderClass}`;
    };

    const selectableData = isItemSelectable ? data?.filter(isItemSelectable) : data;
    const isAllSelected = selectableData?.length > 0 && selectableData.every((item) => selectedItems.has(getItemId(item)));

    return (
        <div className={`text-card-foreground rounded-xl bg-white border border-border shadow dark:border-border overflow-hidden ${minHeight} ${className}`}>
            <Table className={`${tableClassName}`}>
                <TableHeader className='*:last-child:border-b-0 bg-slate-50'>
                    <TableRow ref={headerRowRef} className='bg-muted/50 dark:bg-muted border-b-0! hover:bg-muted/50 dark:hover:bg-muted'>
                        {showCheckbox && (
                            <TableHead className="p-3 w-12 text-center">
                                <input
                                    type="checkbox"
                                    aria-label="Select all"
                                    checked={isAllSelected}
                                    onChange={(e) => onSelectAll?.(e.target.checked)}
                                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer accent-primary align-middle"
                                />
                            </TableHead>
                        )}
                        {columns?.map((column, colIdx) => (
                            <TableHead
                                key={column.key}
                                className={`p-3 text-slate-800 text-xs font-bold uppercase ${column?.headerClassName || ''} ${getStickyClassName(colIdx, true)}`}
                                style={getStickyStyle(colIdx, true)}
                            >
                                {column.header}
                            </TableHead>
                        ))}
                        {/* {actions && <TableHead className='p-3 text-muted-foreground text-xs text-center'>ACTIONS</TableHead>} */}
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {loading ? (
                        <TableLoader
                            columns={columns?.length + (showCheckbox ? 1 : 0) + (actions ? 1 : 0)}
                            rows={5}
                        />
                    ) : data?.length === 0 ? (
                        <TableRow className="hover:bg-transparent dark:hover:bg-transparent">
                            <TableCell
                                colSpan={columns.length + (showCheckbox ? 1 : 0) + (actions ? 1 : 0)}
                                className="p-12 text-center align-middle"
                            >
                                <div className="flex flex-col items-center justify-center w-full">
                                    <Database className="h-10 w-10 text-muted-foreground" />
                                    <div className="mt-3 font-medium text-base text-muted-foreground">
                                        {emptyMessage}
                                    </div>
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : (
                        data?.map((item, index) => {
                            const itemId = getItemId(item);
                            const isSelectable = isItemSelectable ? isItemSelectable(item) : true;
                            const isSelected = isSelectable && selectedItems.has(itemId);

                            return (
                                <TableRow key={itemId || index} className="align-middle">
                                    {showCheckbox && (
                                        <TableCell className="p-3 text-center">
                                            <input
                                                type="checkbox"
                                                aria-label={`Select item ${itemId}`}
                                                checked={isSelected}
                                                disabled={!isSelectable}
                                                onChange={(e) => {
                                                    if (isSelectable) {
                                                        onSelectItem?.(item, e.target.checked, index);
                                                    }
                                                }}
                                                className={`h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary align-middle ${
                                                    isSelectable
                                                        ? "cursor-pointer accent-primary"
                                                        : "opacity-40 cursor-not-allowed bg-slate-100"
                                                }`}
                                                title={!isSelectable ? "Salary already created for this cycle" : undefined}
                                            />
                                        </TableCell>
                                    )}

                                    {columns?.map((column, colIdx) => (
                                        <TableCell
                                            key={`${itemId}-${column.key}`}
                                            className={`p-3 ${column?.className || ''} ${getStickyClassName(colIdx)}`}
                                            style={getStickyStyle(colIdx)}
                                        >
                                            {column?.render
                                                ? column.render(item, index)
                                                : item[column.key]}
                                        </TableCell>
                                    ))}

                                    {actions && (
                                        <TableCell className="p-3 justify-end">
                                            <div className="flex gap-2">
                                                {(typeof actions === 'function' ? actions(item) : actions)?.map((action, i) => (
                                                    <button
                                                        key={i}
                                                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                                        onClick={() => action.onClick(item)}
                                                        title={action.text}
                                                    >
                                                        {action.icon}
                                                    </button>
                                                ))}
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

export default CommonTable;
